'use client';

import DiscountLabel from '@/components/atoms/DiscountLabel';
import PrescriptionDialog, {
	BdDialog,
	ColdDialog,
	DeliveryDialog,
	DhakaDialog
} from '@/components/molecules/ProductDialogs';
import { Product } from '@/services/ecommerce/types/productTypes';
import { shimmer, toBase64 } from '@/services/ecommerce/utils/LoadingImage';
import { generateProductTitle } from '@/services/ecommerce/utils/MetaGenerator';
import {
	checkDeliveryAndStock,
	generateProductLink,
	getProductImage,
	isFlashSale,
	ProductSummary
} from '@/services/ecommerce/utils/helper';
import { imageLoader } from '@/services/ecommerce/utils/imageLoader';
import cls from 'classnames';
import { get } from 'lodash';
import Image from 'next/image';
import Link from 'next/link';
import AddToCart from '../../molecules/AddToCart';
import styles from './ProductCardHorizontal.module.scss';

interface ProductCardProps {
	data: Product;
	addToCart?: boolean;
	size?: 'small' | 'large';
	onClickButton?: React.ReactNode;
	showDelivery?: boolean;
	addToCartProps?: any;
}

const ProductCardHorizontal = ({
	data,
	addToCart = true,
	size = 'large',
	onClickButton,
	showDelivery,
	addToCartProps
}: ProductCardProps) => {
	const { p_name, p_brand, p_strength, p_form, pv } = data;
	const { discountPercentage, mrp, price, variant, unit_label } = ProductSummary(get(pv, '[0]', {}));
	const { stock, isExpress } = checkDeliveryAndStock(data, variant);

	return (
		<div className={cls(styles.card, styles[size])}>
			<div className={styles.cardBody}>
				<div
					className='rounded-9 '
					style={{
						position: 'relative'
					}}>
					<Link
						href={generateProductLink(data, variant)}
						style={{
							...(!stock && {
								opacity: 0.5
							})
						}}>
						<Image
							loader={imageLoader}
							unoptimized={true}
							placeholder='blur'
							blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(117, 117))}`}
							width={117}
							height={117}
							src={getProductImage(data)}
							alt={generateProductTitle(data)}
							className={styles.cardImage}
							style={{
								objectFit: 'contain'
							}}
						/>
						<DiscountLabel
							discountPercentage={discountPercentage}
							p_is_flash_sale={isFlashSale(variant)}
							price={price}
							mrp={mrp}
							size={size}
						/>
					</Link>

					{!stock && (
						<div className={styles.outofstock}>
							<div className={styles.stocks_message}>Out of stock</div>
						</div>
					)}
				</div>
				<div>
					<div className={styles.cardHeader}>
						<Link href={generateProductLink(data, variant)}>{p_name}</Link>
					</div>
					<div className={styles.cardBody}>
						<div className={styles.cardBodyLeft}>
							<div className='text-14 text-grey500 fw-400'>
								{p_strength} {p_form ? ` - ${p_form}` : ''}
							</div>

							<div className='text-14 text-grey700 fw-400'>{p_brand as any}</div>
							{/* <div className='text-14 text-grey700 fw-400'> {p_brand} </div> */}
							<div className='text-14 text-grey500 fw-300'>{unit_label}</div>
						</div>
					</div>
					<div className={styles.cardFooter}>
						<span className={styles.price}>৳{price}</span>
						<span className={styles.mrp}>৳{mrp}</span>
						<span className={styles.profit_percent}>{discountPercentage} % OFF</span>
					</div>
				</div>
			</div>
			<div className={styles.right}>
				<div
					className='d-flex'
					style={{
						alignItems: 'end',
						display: 'flex',
						justifyContent: 'end'
					}}>
					{data?.p_note1?.includes('শুধুমাত্র ঢাকা শহরে ডেলিভারি') ? <DhakaDialog /> : <BdDialog />}
					{!!get(data, 'p_rx_req') && <PrescriptionDialog />}
					{!!get(data, 'p_cold') && <ColdDialog />}
					{showDelivery && stock && <DeliveryDialog isRegular={!isExpress} className={styles.deliveryDialog} />}
				</div>
				{addToCart && (
					<AddToCart
						product={data}
						productRawData={data}
						variant={get(pv, '[0]')}
						inputStyle={{
							minWidth: 'auto',
							...addToCartProps?.inputStyle
						}}
						buttonStyle={addToCartProps.buttonStyle}
						label={<span className='p-14'>{addToCartProps?.label || 'Add to Cart'} </span>}
					/>
				)}
				{onClickButton && onClickButton}
			</div>
		</div>
	);
};

export default ProductCardHorizontal;
